import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { FloatingInputModes } from "./FloatingInputModes";
import { useFluidForm, useFormInstance } from "./FormContext";
import { ClassicForm, CardsForm, InmersiveForm } from "./layouts";
import { FluidChat } from "./FluidChat.component";
import { EmbeddedStyles } from "./EmbeddedStyles";
import { DomUpdateHighlighter } from "./DomUpdateHighlighter";
import { FluidValidation } from "./FluidValidation";
import { FormSubmittedMessage } from "./FormSubmittedMessage";
import { updateCurrentDataWithConversationOutput, getChatOptionsFromConfig } from "../../../shared";
import { useFormInstanceSelect, useFormListener } from "./FormInstance.service";
import { useInlineValidation as useInstantValidation, useUpdateChatOnFieldChange } from "./common";
const FormContainer = styled.div.attrs({ className: "fluid-form-container @container/fluid-form-container" }).withConfig({
    shouldForwardProp: (prop) => !['chatPosition', 'hideInputModes', 'headless'].includes(prop),
}) `

  ${props => props.headless ? 'margin: 0; padding: 0; border: none; box-shadow: none; background: transparent; height: 100%;' : ''}
  ${props => !props.headless ? `border-radius: 5px;
  position: relative;
  padding: 16px;
  padding-top: 0px;
  margin-top: 0px;` : ''}

  ${props => {
    if (!props.chatPosition || props.chatPosition === "floating")
        return '';
    switch (props.chatPosition) {
        case 'left':
        case 'right':
            return `
          display: flex;
          gap: 1rem;
          height: 100%;
          flex-direction: ${props.chatPosition === 'left' ? 'row-reverse' : 'row'};
          
          @media (max-width: 768px) {
            flex-direction: column;
            height: auto;
          }
        `;
        case 'top':
        case 'bottom':
            return `
          display: flex;
          flex-direction: ${props.chatPosition === 'top' ? 'column-reverse' : 'column'};
          gap: 1rem;
          height: 100%;
        `;
        default:
            return '';
    }
}}
`;
const FormSection = styled.div.attrs({
    className: 'fluid-form-section'
}).withConfig({
    shouldForwardProp: (prop) => !['chatPosition'].includes(prop),
}) `
  ${props => {
    if (!props.chatPosition || props.chatPosition === "floating")
        return '';
    switch (props.chatPosition) {
        case 'left':
        case 'right':
            return `
          flex: 1;
          min-width: 0;
        `;
        case 'top':
        case 'bottom':
            return `
          flex: 1;
          min-height: 0;
        `;
        //Calculate container height for wide
        default:
            return '';
    }
}}
`;
const ChatSection = styled.div.attrs({
    className: 'fluid-chat-section min-h-400 h-dvh'
}).withConfig({
    shouldForwardProp: (prop) => !['chatPosition', 'headless'].includes(prop),
}) `

  ${props => props.headless ? 'border: none; box-shadow: none; background: transparent;' : ''}
  ${props => !props.headless ? 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); background: white;   border-radius: 0.75rem; max-height: calc(100dvh - 150px);' : ''}
  
  display: flex;
  flex-direction: column;
  
  ${props => {
    switch (props.chatPosition) {
        case 'left':
        case 'right':
            return `
          width: 400px;
          min-width: 300px;
          min-height: 400px;
          overflow: hidden;
          position: sticky;
          top: 1rem;
          
          @media (max-width: 768px) {
            width: 100%;
            height: 400px;
            margin-top: 1rem;
          }
        `;
        case 'top':
        case 'bottom':
            return `
          height: 300px;
          min-height: 250px;
          width: 100%;
          overflow: hidden;
          
          @media (max-width: 768px) {
            height: 250px;
          }
        `;
        case 'wide':
            return `

          min-height: 250px;
          width: 100%;
          overflow: hidden;
        `;
        default:
            return `
          overflow: hidden;
          border: none;
          box-shadow: none;
          background: transparent;
        `;
    }
}}
`;
export const DocFluidForm = ({ showSubmitButton = true, onSubmit, validationResults, setStage, headless, ...props }) => {
    const form = useFluidForm();
    useFormInstanceSelect((state) => state.submissionId);
    const stage = useFormInstanceSelect((state) => state.submissionStage || "draft");
    const handleSubmit = (data, currentStage) => {
        onSubmit?.(data);
    };
    const showReviewScreen = stage == "review";
    return React.createElement("div", { style: { position: 'relative', ...(headless ? { height: '100%' } : {}) } },
        (stage == "draft") && React.createElement(FormComponent, { ...props, showSubmitButton: showSubmitButton, onSubmit: (data) => handleSubmit(data, stage), headless: headless }),
        showReviewScreen && (React.createElement(EmbeddedStyles, null,
            React.createElement(FluidValidation, { onSubmitForm: () => handleSubmit(form.getFormSubmission(), "review") }))),
        stage == "finalized" && (React.createElement(EmbeddedStyles, null,
            React.createElement(FormSubmittedMessage, { form: form }))));
};
export const FormComponent = ({ onSubmit, showSubmitButton = true, onChange, disableUpdateHighlighting = false, includeChat = false, rounded = true, hideInputModes = false, headless = false, fieldProps, useUpdateChat = true, chatProps }) => {
    const form = useFluidForm();
    useUpdateChatOnFieldChange(useUpdateChat);
    const [update, setUpdate] = useState(0);
    const instanceId = useFormInstance();
    const formWrapperRef = useRef(null);
    const layout = form.getAdditionalConfigValue("styleConfig", "layout") || "form";
    const formStyle = form.getAdditionalConfigValue("styleConfig", "style") || "classic";
    const inputModes = form.getAdditionalConfigValue("styleConfig", "inputModes") || [];
    let chatPosition = form.getAdditionalConfigValue("styleConfig", "chatPosition");
    if (layout == "chat") {
        chatPosition = "wide";
    }
    else if (formStyle == "cards") {
        chatPosition = "floating";
    }
    else if (!inputModes.includes("chat")) {
        chatPosition = "floating";
    }
    const [shouldShowChat, setShouldShowChat] = useState(includeChat || layout == "chat" || inputModes.includes("chat"));
    const formInputModesPosition = form.getAdditionalConfigValue("styleConfig", "formInputModesPosition") || "top";
    const hideInputModesInForm = hideInputModes || formInputModesPosition == 'none' || (shouldShowChat && chatPosition != "floating");
    // Determine if we should show chat and how
    const updateChatEnabled = () => {
        const inputModes = form.getAdditionalConfigValue("styleConfig", "inputModes") || [];
        const layout = form.getAdditionalConfigValue("styleConfig", "layout") || "form";
        let chatEnabled = includeChat || layout == "chat" || inputModes.includes("chat");
        setShouldShowChat(chatEnabled);
    };
    const handleSubmit = async () => {
        onSubmit?.(form.getFormSubmission());
    };
    useFormListener("chatbotService.askFormQuestion", (a1, a2, a3) => {
        setShouldShowChat(true);
    });
    useEffect(() => {
        form.onUpdated((data) => {
            onChange?.(form.getFormSubmission());
        });
        form.events.on("onConfigChange", "DocFluidForm", (data) => {
            setUpdate(update + 1);
            updateChatEnabled();
        });
    }, []);
    const handleMessageReceived = (message) => {
        let current = form.getFormSubmission();
        let merged = updateCurrentDataWithConversationOutput(current, message);
        form.setSubmissionData(merged);
        let firstField = message.messageFields?.[0];
        if (firstField) {
            form.setCurrentField(firstField);
        }
    };
    let formWrapperClasses = (rounded && !headless) ? "f-border rounded-xl p-3 sm:p-5 f-bg shadow-lg relative" : "";
    //Tailwind classes
    let chatContainerClasses = chatPosition == "wide" ? "" : "";
    const title = chatPosition == "floating" ? 'Form AI' : (form.getFormConfig().label || 'Form');
    const chatOptions = getChatOptionsFromConfig(form);
    const autoStartFormChat = shouldShowChat && chatPosition != 'floating' && chatOptions.autoStart !== false;
    return (React.createElement(EmbeddedStyles, { headless: headless },
        fieldProps?.useInstantValidation && React.createElement(FieldInstantValidation, null),
        React.createElement(FormContainer, { chatPosition: chatPosition, hideInputModes: hideInputModesInForm, headless: headless },
            layout == "form" && React.createElement(FormSection, { chatPosition: chatPosition },
                React.createElement("div", { ref: formWrapperRef, className: formWrapperClasses, style: { position: 'relative' } },
                    !hideInputModesInForm && (React.createElement(FloatingInputModes, { layout: "horizontal", inputModes: inputModes, spacing: "md", alignTooltips: "right", background: "black", formRef: formWrapperRef, initialOffset: { top: 12, right: 12 } })),
                    React.createElement("div", { className: "fluidForm-content fluid-form-fields" },
                        React.createElement(DomUpdateHighlighter, null,
                            formStyle == "cards" && React.createElement(CardsForm, { onSubmit: handleSubmit }),
                            formStyle == "inmersive" && React.createElement(InmersiveForm, { onSubmit: handleSubmit }),
                            formStyle == "classic" && React.createElement(ClassicForm, { onSubmit: showSubmitButton ? handleSubmit : undefined, fieldProps: fieldProps }))))),
            shouldShowChat && (React.createElement(ChatSection, { chatPosition: chatPosition, className: chatContainerClasses, headless: headless },
                React.createElement(FluidChat, { ...chatProps, formKey: form.getFormKey(), instanceId: instanceId, headless: headless, title: title, onMessageReceived: handleMessageReceived, rounded: true, inputModes: inputModes, floating: chatPosition == "floating", onClose: () => setShouldShowChat(false), autoStartForm: autoStartFormChat }))))));
};
function FieldInstantValidation() {
    useInstantValidation();
    return null;
}
